import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { WhatsApp, AccessTime, Refresh, CheckCircle } from '@material-ui/icons'
import { DateTime } from 'luxon'

import BasicCard from '../../components/BasicCard'
import Loading from '../../components/Loading'
import SendWhatsappMessage from '../../services/SendWhatsappMessage'
import { getWhatsappQueue, addConsultantContact } from '../../services/utils'
import { errorToast, successToast } from '../../helpers/ToastHelper'

import '../../styles/containers/Sales/WhatsappQueue.css'

// Régua do teste grátis: D1/D5/D9 são toques manuais da Talita pelo WhatsApp,
// nunca automáticos — esta tela ("Contatos do Dia") só organiza a fila e prepara a mensagem.
const TOUCH_META = {
  d1_no_client: { label: 'D1 · Primeiro contato', className: 'touch-badge-d1', statKey: 'd1' },
  d1_has_client: { label: 'D1 · Primeiro contato', className: 'touch-badge-d1', statKey: 'd1' },
  d5: { label: 'D5 · Quente', className: 'touch-badge-d5', statKey: 'd5' },
  d9: { label: 'D9 · Decisão', className: 'touch-badge-d9', statKey: 'd9' }
}

const itemKey = item => `${item.consultantId}-${item.touch}`

// "Horário do WhatsApp: de segunda a sexta, 9h às 11h ou 14h às 17h" (régua do Victor)
const isWithinContactWindow = () => {
  const now = new Date()
  const day = now.getDay() // 0 domingo ... 6 sábado
  const hour = now.getHours()
  if (day === 0 || day === 6) return false
  return (hour >= 9 && hour < 11) || (hour >= 14 && hour < 17)
}

const statCardContent = (value, label, modifierClass, isActive, onClick) => (
  <div
    className={`whatsapp-queue-stat-card ${modifierClass} ${isActive ? 'stat-active' : ''}`}
    onClick={onClick}
    role='button'
    tabIndex={0}
  >
    <h3 className='whatsapp-queue-stat-number'>{value}</h3>
    <p className='whatsapp-queue-stat-label'>{label}</p>
  </div>
)

const queueItemContent = (item, contactingId, isDone, onContact) => {
  const meta = TOUCH_META[item.touch]
  return (
    <div className={`whatsapp-queue-item ${isDone ? 'whatsapp-queue-item-done' : ''}`}>
      <div className='whatsapp-queue-item-header'>
        <span className={`whatsapp-queue-badge ${meta.className}`}>{meta.label}</span>
        <span className='whatsapp-queue-item-name'>{item.name}</span>
        {isDone && (
          <span className='whatsapp-queue-done-tag'>
            <CheckCircle className='whatsapp-queue-done-tag-icon' />
            Contato feito
          </span>
        )}
      </div>
      <p className='whatsapp-queue-item-message'>{item.message}</p>
      <button
        className={`whatsapp-queue-contact-button ${isDone ? 'whatsapp-queue-contact-button-done' : ''}`}
        disabled={contactingId === item.consultantId || isDone}
        onClick={() => onContact(item)}
      >
        {isDone ? (
          <>
            <CheckCircle className='whatsapp-queue-contact-icon' />
            Contato registrado
          </>
        ) : (
          <>
            <WhatsApp className='whatsapp-queue-contact-icon' />
            {contactingId === item.consultantId ? 'Registrando...' : 'Chamar no WhatsApp'}
          </>
        )}
      </button>
    </div>
  )
}

const WhatsappQueue = props => {
  const [queue, setQueue] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [contactingId, setContactingId] = useState(null)
  const [doneKeys, setDoneKeys] = useState(new Set())
  const [activeFilter, setActiveFilter] = useState(null) // null (todas) | 'd1' | 'd5' | 'd9'

  const fetchQueue = useCallback(async () => {
    try {
      const data = await getWhatsappQueue(props.token)
      setQueue(data)
      setDoneKeys(new Set())
    } catch (e) {
      console.error(e)
      errorToast('Não foi possível carregar os contatos do dia, tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }, [props.token])

  useEffect(() => {
    setIsLoading(true)
    fetchQueue()
  }, [fetchQueue])

  // Contadores dos cards do topo contam só quem ainda falta chamar — é o que a Talita
  // precisa bater o olho e entender de cara.
  const counts = useMemo(() => {
    return queue.reduce(
      (acc, item) => {
        if (doneKeys.has(itemKey(item))) return acc
        acc.total += 1
        acc[TOUCH_META[item.touch].statKey] += 1
        return acc
      },
      { total: 0, d1: 0, d5: 0, d9: 0 }
    )
  }, [queue, doneKeys])

  // Pendentes primeiro, já contatadas ficam visíveis mas descem pro fim da lista.
  const visibleQueue = useMemo(() => {
    const filtered = activeFilter
      ? queue.filter(item => TOUCH_META[item.touch].statKey === activeFilter)
      : queue

    return [...filtered].sort((a, b) => {
      const aDone = doneKeys.has(itemKey(a)) ? 1 : 0
      const bDone = doneKeys.has(itemKey(b)) ? 1 : 0
      return aDone - bDone
    })
  }, [queue, activeFilter, doneKeys])

  const toggleFilter = statKey => {
    setActiveFilter(prev => (prev === statKey ? null : statKey))
  }

  const handleContact = async item => {
    SendWhatsappMessage(item.phone, item.message)
    setContactingId(item.consultantId)

    try {
      await addConsultantContact(props.token, {
        contactDate: DateTime.local().toISODate(),
        contactDetails: `Contato via Contatos do Dia (${TOUCH_META[item.touch].label})`,
        consultantId: item.consultantId
      })
      setDoneKeys(prev => new Set(prev).add(itemKey(item)))
      successToast('Contato registrado!')
    } catch (e) {
      console.error(e)
      errorToast('Mandei a mensagem, mas não consegui registrar o contato. Registre manualmente na aba de Consultoras em Teste.')
    } finally {
      setContactingId(null)
    }
  }

  const doneCount = doneKeys.size

  return (
    <div className='content-screen-container general-screen-container'>
      <div className='main-screen-header-container whatsapp-queue-header'>
        <div>
          <h2>Contatos do Dia</h2>
          {queue.length > 0 && (
            <p className='whatsapp-queue-progress'>{doneCount} de {queue.length} já contatadas hoje</p>
          )}
        </div>
        <button className='whatsapp-queue-refresh-button' onClick={fetchQueue}>
          <Refresh className='whatsapp-queue-refresh-icon' />
          Atualizar
        </button>
      </div>

      {!isWithinContactWindow() && (
        <div className='whatsapp-queue-window-banner'>
          <AccessTime className='whatsapp-queue-window-banner-icon' />
          Fora do horário recomendado de contato (seg a sex, 9h–11h e 14h–17h)
        </div>
      )}

      <div className='whatsapp-queue-stats-row'>
        <BasicCard cardContent={statCardContent(counts.total, 'Na fila hoje', 'stat-total', activeFilter === null, () => setActiveFilter(null))} />
        <BasicCard cardContent={statCardContent(counts.d1, 'D1 · Primeiro contato', 'stat-d1', activeFilter === 'd1', () => toggleFilter('d1'))} />
        <BasicCard cardContent={statCardContent(counts.d5, 'D5 · Quentes', 'stat-d5', activeFilter === 'd5', () => toggleFilter('d5'))} />
        <BasicCard cardContent={statCardContent(counts.d9, 'D9 · Decisão', 'stat-d9', activeFilter === 'd9', () => toggleFilter('d9'))} />
      </div>

      {isLoading ? (
        <Loading />
      ) : queue.length === 0 ? (
        <div className='whatsapp-queue-empty-state'>
          <p className='big-paragraph'>Nenhuma consultora pra chamar hoje 🎉</p>
        </div>
      ) : visibleQueue.length === 0 ? (
        <div className='whatsapp-queue-empty-state'>
          <p className='big-paragraph'>Ninguém nesse filtro agora.</p>
        </div>
      ) : (
        <div className='whatsapp-queue-list'>
          {visibleQueue.map(item => (
            <BasicCard
              key={itemKey(item)}
              cardContent={queueItemContent(item, contactingId, doneKeys.has(itemKey(item)), handleContact)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = state => {
  const { user } = state
  return {
    user: user.user,
    token: user.token,
    adminArea: user.adminArea
  }
}

export default connect(mapStateToProps)(WhatsappQueue)
